import {PointDefinition, GeometryHelper, Segment} from "./geometry.helper";

/**
 * You won't find this anywhere on the internet. The rotate function given to gradientTransform can get 3 parameters
 * First is the angle in degrees. Second and third define the point of rotation.
 * Values are 0 0 for top left, 0 1 for left bottom, 1 1 for right bottom, 1 0 for top right.
 * Default is 0 0. The center is 0.5 0.5.
 * Example, to rotate a gradient with 35 degrees around the center: gradientTransform="rotate(35 0.5 0.5)"
 *
 * Otherwise you can define an already rotated gradient like on answer in this stackoverflow
 * but i think this only works for squares
 * https://stackoverflow.com/questions/9025678/how-to-get-a-rotated-linear-gradient-svg-for-use-as-a-background-image
 *
 * Other resources: https://developer.mozilla.org/en-US/docs/Web/SVG/Tutorial/Gradients
 */
export function gradientTransformRotateProp(startingPoint: PointDefinition, targetPoint: PointDefinition) {
    return `rotate(${gradientRotationAngle(startingPoint, targetPoint)} 0.5 0.5)`;
}
function gradientRotationAngle(startingPoint: PointDefinition, targetPoint: PointDefinition) {
    let angle = GeometryHelper.slopeToAngle(GeometryHelper.slope(startingPoint, targetPoint));
    if (startingPoint.x > targetPoint.x || (startingPoint.x === targetPoint.x && startingPoint.y > targetPoint.y)) {
        angle += 180;
    }
    return angle;
}
export function computeSpotlightSVGPath(startingPoint: PointDefinition, targetPoint: PointDefinition, spotlightRadius: number) {
    let extendedSpotlightRadius = spotlightRadius + 6;
    // if the distance between the starting point and the target point is smaller than the spotlight diameter (spotlightradius x 2)
    // then we don't bother with the calculus, we just make a circle.
    // We keep this path as a default in case something breaks at computation.
    // This is how you draw a circle as a path
    // https://www.smashingmagazine.com/2019/03/svg-circle-decomposition-paths/
    // https://codepen.io/jakob-e/pen/bgBegJ
    let dPathProp = `M ${targetPoint.x - extendedSpotlightRadius} ${targetPoint.y} a ${extendedSpotlightRadius} ${extendedSpotlightRadius} 0 0 0 ${extendedSpotlightRadius * 2} 0 a ${extendedSpotlightRadius} ${extendedSpotlightRadius} 0 0 0 -${extendedSpotlightRadius * 2} 0 Z`;

    if (GeometryHelper.distanceBetweenPoints(startingPoint, targetPoint) > extendedSpotlightRadius * 1.2) {
        try {
            let OA = new Segment(startingPoint, targetPoint);
            // we need to determine the coordinates of point D, intersection of T1T2 and OA
            // square length of segment OD
            let quadExtendedSpotlightRadius = Math.pow(extendedSpotlightRadius, 2);
            // oa_od_ratio id the length of segment OD divided to the length of the segment OA
            let oa_od_ratio = quadExtendedSpotlightRadius / OA.quadLength;
            // using oa_od_ratio we get the distance from the axis OA (more specific the distance from D) where the tangents line touches the circle
            let dt_length = Math.sqrt(quadExtendedSpotlightRadius*(1 - oa_od_ratio));
            // also, using the same oa_od_ratio we compute the coordinates of point D
            let D = GeometryHelper.getPointInSegment(targetPoint, startingPoint, oa_od_ratio);
            // using the slope perpendicular to the axis of the spotlight, given a point on that axis (D) get the 2 points that are at a given distance
            let [T1, T2] = getThePointsAtGivenDistanceFromAReferencePointInTheDirectionOfASlope(D, dt_length, OA.slopePerpendicular);
            [T1, T2] = orderPoints(T1, T2, startingPoint, D);
            // the final shape should be: starting from the startingPoint draw a line to T1, then a arc of a circle to T2 (radius is spotlight)
            // then close the shape with a line back to the startingPoint
            dPathProp = `M ${startingPoint.x} ${startingPoint.y} L ${T1.x} ${T1.y} A ${extendedSpotlightRadius} ${extendedSpotlightRadius} 0 1 0 ${T2.x} ${T2.y} Z`;
        } catch (e) {
            throw e;
        }
    }
    // IMPORTANT: The syntax for an arc path is "A rx ry x-axis-rotation large-arc-flag sweep-flag x y"
    // or "a rx ry x-axis-rotation large-arc-flag sweep-flag dx dy" as explained here
    // https://developer.mozilla.org/en-US/docs/Web/SVG/Tutorial/Paths#arcs
    // apparently the inner circle must have an opposite sweep-flag for the fill property to work correctly.
    // https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/fill-rule
    // fillRule="evenodd" changes the behavior but for good measure I take the direction drawing into account
    dPathProp += ` M ${targetPoint.x - spotlightRadius} ${targetPoint.y} a ${spotlightRadius} ${spotlightRadius} 0 0 1 ${spotlightRadius * 2} 0 a ${spotlightRadius} ${spotlightRadius} 0 0 1 -${spotlightRadius * 2} 0 Z`

    return dPathProp;
}

function getThePointsAtGivenDistanceFromAReferencePointInTheDirectionOfASlope(reference: PointDefinition, distance: number, slope: number) {
    let C: PointDefinition = {x: 0, y: 0}, D: PointDefinition = {x: 0, y: 0};
    if (slope === Infinity) {
        C = {
            x: reference.x,
            y: reference.y - distance
        };
        D = {
            x: reference.x,
            y: reference.y + distance
        };
        return [C, D];
    }

    let temp1 = Math.sqrt(Math.pow(distance, 2)/(Math.pow(slope, 2) + 1));
    let Cx = reference.x - temp1;
    C = {
        x: Cx,
        y: (Cx - reference.x)*slope + reference.y
    };
    let Dx = reference.x + temp1;
    D = {
        x: Dx,
        y: (Dx - reference.x)*slope + reference.y
    };
    return [C, D];
}

/**
 * The points C and D where calculated and are at the same distance from the line OA.
 * One point is on a side of the line OA and the other on the other side.
 * We need to know which one is on the left of OA considering the direction of the OA line is from O towards A
 * The function returns [C, D] if C is on the left and D is on the right and [D, C] otherwise
 */
function orderPoints(Cc: PointDefinition, Dd: PointDefinition, O: PointDefinition, A: PointDefinition) {
    let C = {x: Cc.x, y: Cc.y};
    let D = {x: Dd.x, y: Dd.y};
    if (A.x > O.x) {
        if (A.y > O.y) {
            // first point has x < A.x and y > A.y
            // the second point has x > A.x and y < A.y
            if (C.x < A.x && C.y > A.y && D.x > A.x && D.y < A.y) {
                return [C, D];
            }
            if (D.x < A.x && D.y > A.y && C.x > A.x && C.y < A.y) {
                return [D, C];
            }
            throw Error(`Points provided are invalid (1). C: ${C.x}, ${C.y}. D: ${D.x}, ${D.y}. O: ${O.x}, ${O.y}. A: ${A.x}, ${A.y}.`);
        } else if (A.y < O.y) {
            // first point has x > A.x and y > A.y
            // the second point has x < A.x and y < A.y
            if (C.x > A.x && C.y > A.y && D.x < A.x && D.y < A.y) {
                return [C, D];
            }
            if (D.x > A.x && D.y > A.y && C.x < A.x && C.y < A.y) {
                return [D, C];
            }
            throw Error(`Points provided are invalid (2). C: ${C.x}, ${C.y}. D: ${D.x}, ${D.y}. O: ${O.x}, ${O.y}. A: ${A.x}, ${A.y}.`);
        } else if (A.y === O.y) {
            // first point has y > A.y
            // the second point has y < A.y
            if (C.y > A.y && D.y < A.y) {
                return [C, D];
            }
            if (D.y > A.y && C.y < A.y) {
                return [D, C];
            }
            throw Error(`Points provided are invalid (7). C: ${C.x}, ${C.y}. D: ${D.x}, ${D.y}. O: ${O.x}, ${O.y}. A: ${A.x}, ${A.y}.`);
        }
    } else if (A.x < O.x) {
        if (A.y > O.y) {
            // first point has x < A.x and y < A.y
            // the second point has x > A.x and y > A.y
            if (C.x < A.x && C.y < A.y && D.x > A.x && D.y > A.y) {
                return [C, D];
            }
            if (D.x < A.x && D.y < A.y && C.x > A.x && C.y > A.y) {
                return [D, C];
            }
            throw Error(`Points provided are invalid (4). C: ${C.x}, ${C.y}. D: ${D.x}, ${D.y}. O: ${O.x}, ${O.y}. A: ${A.x}, ${A.y}.`);
        } else if (A.y < O.y) {
            // first point has x > A.x and y < A.y
            // the second point has x < A.x and y > A.y
            if (C.x > A.x && C.y < A.y && D.x < A.x && D.y > A.y) {
                return [C, D];
            }
            if (D.x > A.x && D.y < A.y && C.x < A.x && C.y > A.y) {
                return [D, C];
            }
            throw Error(`Points provided are invalid (3). C: ${C.x}, ${C.y}. D: ${D.x}, ${D.y}. O: ${O.x}, ${O.y}. A: ${A.x}, ${A.y}.`);
        } else if (A.y === O.y) {
            // first point has y < A.y
            // the second point has y > A.y
            if (C.y < A.y && D.y > A.y) {
                return [C, D];
            }
            if (D.y < A.y && C.y > A.y) {
                return [D, C];
            }
            throw Error(`Points provided are invalid (8). C: ${C.x}, ${C.y}. D: ${D.x}, ${D.y}. O: ${O.x}, ${O.y}. A: ${A.x}, ${A.y}.`);
        }
    } else if (A.x === O.x) {
        if (A.y > O.y) {
            // first point has x < A.x
            // the second point has x > A.x
            if (C.x < A.x && D.x > A.x) {
                return [C, D];
            }
            if (D.x < A.x && C.x > A.x) {
                return [D, C];
            }
            throw Error(`Points provided are invalid (5). C: ${C.x}, ${C.y}. D: ${D.x}, ${D.y}. O: ${O.x}, ${O.y}. A: ${A.x}, ${A.y}.`);
        } else if (A.y < O.y) {
            // first point has x > A.x
            // the second point has x < A.x
            if (C.x > A.x && D.x < A.x) {
                return [C, D];
            }
            if (D.x > A.x && C.x < A.x) {
                return [D, C];
            }
            throw Error(`Points provided are invalid (6). C: ${C.x}, ${C.y}. D: ${D.x}, ${D.y}. O: ${O.x}, ${O.y}. A: ${A.x}, ${A.y}.`);
        }
    }
}
